import { Companies as CompanyIcon } from '@clearkit/icons';
import { CKCompanyLogo, CKDrawer, CKTab, CKTabList, CKTabs } from 'clearkit';
import { useRef } from 'react';
import Helmet from 'react-helmet';

import AttributeBrowser from '~/components/AttributeBrowser';
import LoadingArea from '~/components/LoadingArea';
import CompanyDetails from '~/pages/companies/Show/Details';

import { useCompanyLookup } from './hooks';
import MissingRecord from './MissingRecord';

type CompanyLookupProps = {
  domain: string;
};

const LookupSidebarCompany = ({ domain }: CompanyLookupProps) => {
  const overflowScrollRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (stickyHeaderBounds: any) => {
    const stickyHeaderGap = 28;
    const customOffset =
      stickyHeaderBounds.top - stickyHeaderBounds.height - stickyHeaderGap;
    if (overflowScrollRef.current) {
      overflowScrollRef.current.scrollTo({ top: customOffset });
    }
  };

  const { data, isLoading, isError, refetch } = useCompanyLookup(domain);

  const company = data?.data;

  if (isLoading) return <LoadingArea className="x-sidebar" loading />;
  if (isError)
    return <MissingRecord onClickRefetch={() => refetch()} type="company" />;
  if (!company) return <div className="x-sidebar">Default State</div>;

  return (
    <div className="x-sidebar">
      <Helmet title={company.name} />

      <CKDrawer.Header className="border-none">
        <div className="flex items-center flex-1">
          <div className="mr-4">
            <CKCompanyLogo
              className="shadow-sm"
              cornerRadius={8}
              domain={company.domain}
              size={56}
            />
          </div>

          <div>
            <CKDrawer.Heading>
              {company.name || company.domain || 'Loading...'}
            </CKDrawer.Heading>
            <a
              className="text-blue"
              href={`http://${company.domain}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {company.domain || '...'}
            </a>
          </div>
        </div>
      </CKDrawer.Header>

      <CKTabs
        aria-label="Company tabs"
        className="flex flex-col flex-grow min-h-0"
        index={0}
        isBordered
      >
        <CKTabList
          aria-label="Company tabs"
          className="flex-shrink-0 px-10 -mt-4"
        >
          <CKTab icon={<CompanyIcon />} label="Company details">
            Company details
          </CKTab>
        </CKTabList>

        <div className="overflow-y-auto" ref={overflowScrollRef}>
          <CompanyDetails company={company} />

          <LoadingArea loading={!company?.enrichment}>
            <AttributeBrowser
              enrichment={company.enrichment}
              object={company}
              onTabChange={handleTabChange}
              traits={{}}
            />
          </LoadingArea>
        </div>
      </CKTabs>
    </div>
  );
};

export { LookupSidebarCompany };
